import React, { useState } from "react";
import { Container, Row, Col, ProgressBar, ButtonGroup, Button, Table } from "react-bootstrap";

const SewageTimeline = ({ events, dischargingNow = false }) => {
    const numberDaysDefault = 2;
    const [calculateDays, setCalculateDays] = useState(numberDaysDefault);

    // Constants for calculateDays days in minutes
    const totalMinutes = calculateDays * 24 * 60;
    const timelineStart = new Date();
    timelineStart.setMinutes(timelineStart.getMinutes() - totalMinutes);

    // Helper to calculate the proportion of minutes
    const calculateProportion = (start, end) => {
        const startTime = Math.max(new Date(start).getTime(), timelineStart.getTime());
        const endTime = Math.min(new Date(end).getTime(), Date.now());
        return Math.max(0, (endTime - startTime) / (1000 * 60));
    };


    // Generate segments for discharging and non-discharging periods
    const generateSegments = (events) => {
        const segments = [];
        const filteredEvents = [];
        let currentTime = timelineStart.getTime();
        let totalDischargeTime = 0;
        let dischargeEventCount = 0;
        [...events].sort((a, b) => new Date(a.startTimestamp) - new Date(b.startTimestamp)).forEach(({ startTimestamp, endTimestamp }) => {
            const start = new Date(startTimestamp).getTime();
            const end = new Date(endTimestamp).getTime();

            // Add non-discharging segment if there's a gap
            if (start > currentTime) {
                const gapDuration = (start - currentTime) / (1000 * 60);
                segments.push({
                    duration: gapDuration,
                    color: "white"
                });
            }

            // Add discharging segment
            const dischargeDuration = calculateProportion(start, end);
            if (dischargeDuration > 0) {
                segments.push({
                    duration: dischargeDuration,
                    color: "red"
                });
                totalDischargeTime += dischargeDuration;
                dischargeEventCount += 1;
                filteredEvents.push({
                    startTimestamp: startTimestamp,
                    endTimestamp: endTimestamp,
                    duration: dischargeDuration
                })
            }

            // Update current time
            currentTime = Math.max(currentTime, end);
        });

        if(dischargingNow) {
        	events[events.length-1] = {
        		...events[events.length-1],
        		endTimestamp: (new Date()).toISOString()
        	}
        }

        // Add non-discharging segment if there's time remaining
        if (currentTime < Date.now()) {
            const remainingDuration = (Date.now() - currentTime) / (1000 * 60);
            segments.push({
                duration: remainingDuration,
                color: "white",
            });
        }

        return { segments, totalDischargeTime, dischargeEventCount, filteredEvents };
    };

    const { segments, totalDischargeTime, dischargeEventCount, filteredEvents } = generateSegments(events);

    // Calculate percentages for each segment
    const progressSegments = segments.map((segment) => ({
        percentage: (segment.duration / totalMinutes) * 100,
        color: segment.color,
    }));

    return (
        <>
            <Row>
                <Col xs={12} className="bg-dark header-bold text-white mt-2 mb-2">
                    Discharge Timeline (Last {calculateDays} Day{calculateDays !== 1 ? "s" : ""})
                </Col>
                <Col xs={12}>
                    <ButtonGroup className="mb-3" variant="dark">
                        {[1, 2, 3, 7, 14].map((days) => (
                            <Button
                                key={days}
                                variant={calculateDays === days ? "secondary" : "outline-dark"}
                                onClick={() => setCalculateDays(days)}
                                size="sm"
                            >
                                {days} Day{days !== 1 ? "s" : ""}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Col>
                <Col xs={12}>
                    <ProgressBar style={{ border: "1px solid black" }}>
                        {progressSegments.map((segment, index) => (
                            <ProgressBar
                                key={index}
                                now={segment.percentage}
                                style={{ backgroundColor: segment.color }}
                            />
                        ))}
                    </ProgressBar>
                </Col>
                <Col xs={12}>
                    <span style={{ fontSize: "0.7em" }}><i>Small discharge periods may not be visible on smaller screens or with a larger history shown, offline periods are not shown.</i></span>
                </Col>
                <Col xs={12} className="mt-1">
                    <Table bordered>
                        <thead>
                            <tr>
                                <th className="text-white bg-secondary">Total Discharge Events</th>
                                <th className="text-white bg-secondary">Total Discharge Time (Minutes)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{dischargeEventCount}</td>
                                <td>{Math.round(totalDischargeTime)}</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
                <Col xs={12} className="header-bold text-white bg-secondary">Discharge List</Col>
                <Col xs={12} className="mt-1">
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Duration (minutes)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEvents.length > 0 ? (
                                filteredEvents.map((event, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{new Date(event.startTimestamp).toLocaleString()}</td>
                                        <td>{new Date(event.endTimestamp).toLocaleString()}</td>
                                        <td>{event.duration}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center">
                                        No events available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Col>

                <Col xs={12}>
                    <span style={{ fontSize: "0.7em" }}><i>The count of discharge events displayed here reflects individual discharge events recorded during the selected timeline. These <b>are not</b> aggregated according to the 12/24 method, instead, they represent discrete events where sewage was discharged.</i></span>
                </Col>
            </Row>
        </>
    );
};

export default SewageTimeline;
